<template lang="pug">
.audio-player(:class='classObject')
  audio(:src='src', ref='audio')
</template>

<script lang="ts">
import { Component, Vue, Ref, Prop, Watch } from 'vue-property-decorator'
import '@/assets/css/mediaelement.css'

@Component
export default class AudioPlayer extends Vue {
  // プレイヤーオブジェクト
  private player: any

  // 一時停止フラグ
  private paused = false

  // ボタン押下不可フラグ
  private buttonDisabled = false

  // ボタン表示初期化フラグ
  private buttonInit = false

  // 音声ソース名
  @Prop()
  src!: string

  // インジケータ表示/非表示
  @Prop({ default: true })
  showIndicator!: boolean

  // 一時停止ボタン押下可/不可
  @Prop({ default: true })
  availablePause!: boolean

  // 再再生可/不可
  @Prop({ default: true })
  availableReplay!: boolean

  // 再生回数
  @Prop({ default: 0 })
  playCount!: number

  // audio要素ref
  @Ref() audio!: HTMLElement

  // 音声src変更時
  @Watch('src')
  private onSrcChange() {
    // 表示を初期化する
    this.initCurrentTime()
    this.buttonInit = true
    this.paused = false
    this.buttonDisabled = false
    // 再再生不可設定時、ボタン押下不可チェック
    if (!this.availableReplay) this.buttonDisabled = this.playCount > 0
  }

  private get features(): string[] {
    return this.showIndicator ? ['playpause', 'current', 'progress', 'duration', 'volume'] : ['playpause']
  }

  private get audioWidth(): number {
    return this.showIndicator ? 400 : 40
  }

  get classObject(): { [key: string]: boolean } {
    return {
      'button-inactive': this.buttonDisabled,
      'button-init': this.buttonInit,
    }
  }

  private mounted() {
    // 読み込みタグ重複防止のため一旦削除
    this.removeElementPlayerTag()
    // 読み込みタグを追加
    this.addElementPlayerTag()
    // 再再生不可設定時、再生数が0より大きい場合はボタン押下不可
    if (!this.availableReplay) this.buttonDisabled = this.playCount > 0
    // 各種イベントを設定
    this.audio.addEventListener('play', this.startPlayEvent)
    this.audio.addEventListener('playing', this.duaringPlayEvent)
    this.audio.addEventListener('pause', this.pausePlayEvent)
    this.audio.addEventListener('ended', this.endPlayEvent)
  }

  private beforeDestroy() {
    // 音声を停止
    if (this.player) this.player.remove()
    // 読み込みタグ削除
    this.removeElementPlayerTag()
  }

  // ElementPlayerのcssとjsの読み込みタグ追加
  private addElementPlayerTag() {
    // 読み込みタグを追加する
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = '/student/mediaelement/mediaelementplayer.min.css'
    link.id = 'mediaelement_css'
    // assetsのカスタマイズCSSで上書きできるように、headの先頭に追加
    document.head.prepend(link)
    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.src = '/student/mediaelement/mediaelement-and-player.min.js'
    script.id = 'mediaelement_js'
    document.body.appendChild(script)
    // jsが読み込まれたらElementPlayer初期化実行
    script.onload = () => {
      this.elementPlayerInit()
    }
  }

  // ElementPlayerのcssとjsの読み込みタグ削除
  private removeElementPlayerTag() {
    const link = document.getElementById('mediaelement_css')
    if (link) document.head.removeChild(link)
    const script = document.getElementById('mediaelement_js')
    if (script) document.body.removeChild(script)
  }

  // ElementPlayer初期化
  private elementPlayerInit() {
    // windowが持つ外部jsのオブジェクトはWindow型に定義されていないのでany型として扱う
    this.player = new (window as any).MediaElementPlayer(this.audio, {
      iconSprite: '/student/mediaelement/mejs-controls.svg',
      features: this.features,
      defaultAudioWidth: this.audioWidth,
    })
  }

  // 再生開始時イベント
  private startPlayEvent() {
    if (!this.paused) {
      // 一時停止からの再開時はemitしない
      this.$emit('started', this.playCount + 1)
    }
    this.buttonInit = false
    this.paused = false
  }

  // 再生中イベント
  private duaringPlayEvent() {
    this.buttonDisabled = !this.availablePause
  }

  // 一時停止時イベント
  private pausePlayEvent() {
    this.paused = true
  }

  // 再生完了時イベント
  private endPlayEvent() {
    this.buttonDisabled = !this.availableReplay
    this.paused = false
  }

  // 再生時間表示を初期化
  private initCurrentTime() {
    this.player.setCurrentTime(0)
    if (!this.showIndicator) return
    // インディケーター表示時は、プログレスバーの各要素のtransformスタイルをリセットする
    const timeLoaded = document.getElementsByClassName('mejs__time-loaded')[0] as HTMLElement
    const timeCurrent = document.getElementsByClassName('mejs__time-current')[0] as HTMLElement
    const timeHandle = document.getElementsByClassName('mejs__time-handle')[0] as HTMLElement
    timeLoaded.style.transform = ''
    timeCurrent.style.transform = ''
    timeHandle.style.transform = ''
  }
}
</script>
